import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ResourceCardProps {
  resource: {
    id: string;
    title: string;
    description: string;
    category: string;
    type: string;
    image: string;
    alt: string;
    downloads: number;
    rating: number;
    languages: string[];
    fileSize?: string;
    duration?: string;
    pages?: number;
  };
  onBookmark: (id: string) => void;
  isBookmarked: boolean;
}

const ResourceCard: React.FC<ResourceCardProps> = ({ resource, onBookmark, isBookmarked }) => {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'PDF':
        return 'DocumentTextIcon';
      case 'Video':
        return 'VideoCameraIcon';
      case 'Audio':
        return 'SpeakerWaveIcon';
      case 'Toolkit':
        return 'WrenchScrewdriverIcon';
      default:
        return 'DocumentIcon';
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Peace Education': 'bg-healing-blue text-white',
      'Forgiveness Framework': 'bg-peaceful-teal text-white',
      'Community Organizing': 'bg-transformation-gold text-grounded-charcoal',
      'Research Library': 'bg-primary text-primary-foreground',
      'Media Resources': 'bg-secondary text-secondary-foreground',
      'Personal Transformation': 'bg-accent text-accent-foreground',
    };
    return colors[category] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-[1.02] border border-border">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={resource.image}
          alt={resource.alt}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3">
          <span className={`px-3 py-1 rounded-full text-xs font-cta font-bold ${getCategoryColor(resource.category)}`}>
            {resource.category}
          </span>
        </div>
        <button
          onClick={() => onBookmark(resource.id)}
          className="absolute top-3 right-3 p-2 bg-card rounded-full shadow-md transition-all duration-200 hover:scale-110"
          aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
        >
          <Icon
            name="BookmarkIcon"
            size={20}
            variant={isBookmarked ? 'solid' : 'outline'}
            className={isBookmarked ? 'text-accent' : 'text-muted-foreground'}
          />
        </button>
      </div>

      <div className="p-5">
        <div className="flex items-center space-x-2 mb-3">
          <Icon name={getTypeIcon(resource.type)} size={20} className="text-primary" />
          <span className="text-sm font-body font-medium text-muted-foreground">{resource.type}</span>
        </div>

        <h3 className="text-lg font-headline font-bold text-foreground mb-2 line-clamp-2">
          {resource.title}
        </h3>

        <p className="text-sm font-body text-muted-foreground mb-4 line-clamp-3">
          {resource.description}
        </p>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-1">
            {[...Array(5)].map((_, index) => (
              <Icon
                key={index}
                name="StarIcon"
                size={16}
                variant={index < Math.floor(resource.rating) ? 'solid' : 'outline'}
                className={index < Math.floor(resource.rating) ? 'text-accent' : 'text-muted-foreground'}
              />
            ))}
            <span className="text-sm font-body text-muted-foreground ml-2">
              {resource.rating.toFixed(1)}
            </span>
          </div>

          <div className="flex items-center space-x-1 text-muted-foreground">
            <Icon name="ArrowDownTrayIcon" size={16} />
            <span className="text-sm font-body">{resource.downloads.toLocaleString()}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {resource.languages.map((lang) => (
            <span
              key={lang}
              className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs font-body"
            >
              {lang}
            </span>
          ))}
        </div>

        {(resource.fileSize || resource.duration || resource.pages) && (
          <div className="flex items-center space-x-4 mb-4 text-xs font-body text-muted-foreground">
            {resource.fileSize && (
              <span className="flex items-center space-x-1">
                <Icon name="DocumentIcon" size={14} />
                <span>{resource.fileSize}</span>
              </span>
            )}
            {resource.duration && (
              <span className="flex items-center space-x-1">
                <Icon name="ClockIcon" size={14} />
                <span>{resource.duration}</span>
              </span>
            )}
            {resource.pages && (
              <span className="flex items-center space-x-1">
                <Icon name="DocumentTextIcon" size={14} />
                <span>{resource.pages} pages</span>
              </span>
            )}
          </div>
        )}

        <Link
          href="#"
          className="block w-full py-2.5 bg-primary text-primary-foreground text-center rounded-md font-cta text-sm font-bold transition-all duration-300 hover:bg-secondary hover:scale-105"
        >
          Download Resource
        </Link>
      </div>
    </div>
  );
};

export default ResourceCard;